import { Form, message } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import Swal from "sweetalert2";
import { validateImageFiles } from "../../../../../utils/validateImageFiles";

export const useRoomingList = () => {
  const [rooms, setRooms] = useState([]);
  const [editingRoom, setEditingRoom] = useState(null);
  const [imageFiles, setImageFiles] = useState([]); // For image uploads
  const [manualBeds, setManualBeds] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [form] = Form.useForm();
  const navigate = useNavigate(); // Initialize navigate

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get("/api/rooms/getallrooms");
      setRooms(response.data);
    } catch (error) {
      message.error("Failed to fetch rooms");
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setIsModalVisible(true);
    form.setFieldsValue({
      ...room,
      bedding:
        room.bedding === "Input No of Bed Manually"
          ? "Input No of Bed Manually"
          : room.bedding,
    });
    setImageFiles(
      room.imageurls.map((url, index) => ({
        uid: index,
        name: `Image ${index + 1}`,
        status: "done",
        url,
      }))
    );
    if (room.bedding === "Input No of Bed Manually") {
      setManualBeds(room.manualBeds);
    }
  };

  const handleDelete = async (roomId) => {
    try {
      await axios.delete(`/api/rooms/delete/${roomId}`);
      message.success("Room deleted successfully");
      fetchRooms();
    } catch (error) {
      message.error("Failed to delete room");
    }
  };

  const handleImageChange = ({ fileList }) => {
    const newFiles = fileList.filter((file) => !file.url); // Filter out new files (without url)
    const existingFiles = imageFiles.filter((file) => file.url); // Keep existing files

    // Combine new files with existing ones and set the updated list
    setImageFiles([...existingFiles, ...newFiles]);
  };

  const handleImageRemove = async (file) => {
    if (file.url) {
      try {
        // Remove the image URL from the backend
        await axios.post(`/api/rooms/remove-image/${editingRoom._id}`, {
          imageUrl: file.url,
        });
        message.success("Image removed successfully");

        // Update the imageFiles state immediately to remove the image from the list
        setImageFiles((prevFiles) =>
          prevFiles.filter((img) => img.url !== file.url)
        );
      } catch (error) {
        message.error("Failed to remove image");
      }
    } else {
      // Remove the image from the list (for new files not yet uploaded to the backend)
      setImageFiles((prevFiles) =>
        prevFiles.filter((img) => img.uid !== file.uid)
      );
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const isErrorImage = validateImageFiles({ files: imageFiles });

      if (isErrorImage) {
        Swal.fire({
          title: "Error",
          text: isErrorImage,
          icon: "error",
        });
        return;
      }
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key]) {
          formData.append(key, values[key]);
        }
      });

      // Add image files to form data
      imageFiles.forEach((file) => {
        if (file.originFileObj) {
          formData.append("images", file.originFileObj); // Only append new files
        }
        if (file.url) {
          formData.append("images", file.url); // Only append existing files
        }
      });

      // Append manual bed count if "Input No of Bed Manually" is selected
      if (values.bedding === "Input No of Bed Manually") {
        formData.append("manualBeds", manualBeds);
      }

      if (editingRoom) {
        await axios.put(`/api/rooms/updateroom/${editingRoom._id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Room updated successfully");
      }
      setIsModalVisible(false);
      form.resetFields();
      fetchRooms();
    } catch (error) {
      message.error("Failed to save room");
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  // Reorder rooms in local state after drag-and-drop
  const reorderRooms = (startIndex, endIndex) => {
    setRooms((prev) => {
      const updated = Array.from(prev || []);
      const [removed] = updated.splice(startIndex, 1);
      if (!removed) return prev;
      updated.splice(endIndex, 0, removed);
      // persist new order to backend
      persistReorder(updated).catch(() => {
        message.error("Failed to persist room order");
      });
      return updated;
    });
  };

  // Reorder images in local state after drag-and-drop (client-side only)
  const reorderImages = (startIndex, endIndex) => {
    setImageFiles((prev) => {
      const updated = Array.from(prev || []);
      const [removed] = updated.splice(startIndex, 1);
      if (!removed) return prev;
      updated.splice(endIndex, 0, removed);
      return updated;
    });
  };

  const persistReorder = async (orderedRooms) => {
    // orderedRooms: array of room objects in desired order
    const payload = orderedRooms.map((r, idx) => ({
      _id: r._id,
      order: idx + 1,
    }));
    await axios.put(`/api/rooms/reorder`, payload);
  };

  return {
    form,
    rooms,
    imageFiles,
    manualBeds,
    navigate,
    isModalVisible,
    handleEdit,
    handleDelete,
    handleImageChange,
    handleImageRemove,
    handleModalOk,
    handleModalCancel,
    setManualBeds,
    reorderRooms,
    reorderImages,
  };
};
